import { useCallback, useEffect, useRef, useState } from 'react';
import type { ViewerInputMessage } from '@viewer/protocol';
import { LoginScreen } from '../features/auth/LoginScreen';
import { EnrollDeviceForm } from '../features/home/EnrollDeviceForm';
import { HomeScreen } from '../features/home/HomeScreen';
import { HostSessionScreen } from '../features/host/HostSessionScreen';
import { IncomingRequestDialog } from '../features/host/IncomingRequestDialog';
import { InviteScreen } from '../features/invite/InviteScreen';
import { OutgoingRequestDialog } from '../features/viewer/OutgoingRequestDialog';
import { SettingsScreen, type SettingsTab } from '../features/settings/SettingsScreen';
import { ViewerScreen } from '../features/viewer/ViewerScreen';
import { browserDeviceName } from '../lib/browser';
import { INSIDE_APP, PREVIEW_HOST, SIGNALING_URL } from '../lib/config';
import { controller, useAppState } from '../lib/session/useAppState';
import { getAppInfo, type AppInfo } from '../lib/tauri/commands';
import { Modal } from '../ui/Modal';
import { Notice } from '../ui/Notice';
import { StatusDot } from '../ui/StatusDot';
import styles from './App.module.css';

type Screen = 'home' | 'connections' | 'login' | 'settings';

/** Link de convite aberto no navegador: /convite/<token>. */
function inviteTokenFromUrl(): string | null {
  if (INSIDE_APP) return null;
  const m = /^\/convite\/([A-Za-z0-9_-]+)\/?$/.exec(window.location.pathname);
  return m?.[1] ?? null;
}

/** Sai da tela cheia do viewer (e do Keyboard Lock) para o pedido de acesso ficar visível. */
async function exitAppFullscreen() {
  try {
    navigator.keyboard?.unlock?.();
  } catch {
    // Keyboard Lock pode não existir ou já estar liberado.
  }
  if (document.fullscreenElement) {
    try {
      await document.exitFullscreen();
    } catch {
      // Já saiu ou o navegador bloqueou.
    }
  }
}

function SettingsGearIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7Z"
        stroke="currentColor"
        strokeWidth="1.75"
      />
      <path
        d="M19.4 13a7.7 7.7 0 0 0 .05-2l2.05-1.6-2-3.46-2.45.8a7.6 7.6 0 0 0-1.73-1L14.9 3h-5.8L8.7 5.74a7.6 7.6 0 0 0-1.73 1l-2.45-.8-2 3.46L4.57 11a7.7 7.7 0 0 0 0 2l-2.05 1.6 2 3.46 2.45-.8a7.6 7.6 0 0 0 1.73 1L9.1 21h5.8l.42-2.74a7.6 7.6 0 0 0 1.73-1l2.45.8 2-3.46L19.4 13Z"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="8" r="3.25" stroke="currentColor" strokeWidth="1.75" />
      <path
        d="M5.5 18.5c1.6-3 4-4.5 6.5-4.5s4.9 1.5 6.5 4.5"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
      />
    </svg>
  );
}

function EnrollIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
      <rect x="3.5" y="4.5" width="17" height="15" rx="2" stroke="currentColor" strokeWidth="1.75" />
      <path d="M8 9.5h8M8 13h5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
      <circle cx="17.5" cy="16.5" r="3.25" fill="var(--surface)" stroke="currentColor" strokeWidth="1.75" />
      <path d="M17.5 15v3M16 16.5h3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

export function App() {
  const state = useAppState();
  const [info, setInfo] = useState<AppInfo | null>(null);
  const [screen, setScreen] = useState<Screen>('home');
  const [settingsTab, setSettingsTab] = useState<SettingsTab>('account');
  const [inviteToken, setInviteToken] = useState<string | null>(inviteTokenFromUrl);
  const [focusOrgId, setFocusOrgId] = useState<string | null>(null);
  const [enrollOpen, setEnrollOpen] = useState(false);
  const wasEnrolling = useRef(false);
  const knownHostSessions = useRef<string[]>([]);
  const showConnectionsOnReturn = useRef(false);
  const canHost = INSIDE_APP || PREVIEW_HOST;

  useEffect(() => {
    if (!INSIDE_APP) {
      controller.start(SIGNALING_URL, browserDeviceName());
      return () => controller.stop();
    }
    let cancelled = false;
    getAppInfo()
      .then((appInfo) => {
        if (cancelled) return;
        setInfo(appInfo);
        controller.start(SIGNALING_URL, appInfo.computerName);
      })
      .catch(console.error);
    return () => {
      cancelled = true;
      controller.stop();
    };
  }, []);

  useEffect(() => {
    if (wasEnrolling.current && !state.enrolling && state.notice?.kind === 'info') setEnrollOpen(false);
    wasEnrolling.current = state.enrolling;
  }, [state.enrolling, state.notice]);

  useEffect(() => {
    if (!enrollOpen) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape' && !state.enrolling) setEnrollOpen(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [enrollOpen, state.enrolling]);

  // Login concluído a partir da tela Entrar: volta para onde faz sentido.
  useEffect(() => {
    if (state.user && screen === 'login') {
      if (state.organizations.length > 0) {
        setSettingsTab('overview');
        setScreen('settings');
      } else {
        setScreen('home');
      }
    }
  }, [state.user, state.organizations.length, screen]);

  // Pedido de acesso ou alguém já conectado: sai da tela cheia do viewer (senão o diálogo fica por baixo).
  useEffect(() => {
    if (!state.incoming && state.hostSessions.length === 0) return;
    void exitAppFullscreen();
  }, [state.incoming, state.hostSessions.length]);

  // Alguém novo entrou: se a janela já está em foco, abre Conexões agora; senão, na próxima volta/clique.
  useEffect(() => {
    const ids = state.hostSessions.map((connection) => connection.sessionId);
    const added = ids.some((id) => !knownHostSessions.current.includes(id));
    knownHostSessions.current = ids;
    if (ids.length === 0) {
      showConnectionsOnReturn.current = false;
      return;
    }
    if (!added || !canHost) return;
    if (document.hasFocus()) {
      showConnectionsOnReturn.current = false;
      setScreen('connections');
    } else {
      showConnectionsOnReturn.current = true;
    }
  }, [state.hostSessions, canHost]);

  useEffect(() => {
    if (!canHost) return;
    const showConnections = () => {
      if (!showConnectionsOnReturn.current) return;
      showConnectionsOnReturn.current = false;
      setScreen('connections');
    };
    window.addEventListener('focus', showConnections);
    window.addEventListener('pointerdown', showConnections, true);
    return () => {
      window.removeEventListener('focus', showConnections);
      window.removeEventListener('pointerdown', showConnections, true);
    };
  }, [canHost]);

  // Pedido pendente: já aponta Conexões para o diálogo aparecer no lugar certo.
  useEffect(() => {
    if (state.incoming && canHost) setScreen('connections');
  }, [state.incoming, canHost]);

  const sendInput = useCallback((message: ViewerInputMessage) => controller.sendInput(message), []);
  const onAppPaste = useCallback(() => controller.pasteLocalClipboard(), []);
  const onBrowserPaste = useCallback((data: DataTransfer) => controller.pasteFromBrowser(data), []);

  function closeInvite() {
    setInviteToken(null);
    window.history.replaceState({}, '', '/');
  }

  function openSettings(tab: SettingsTab = 'overview') {
    setSettingsTab(tab);
    setScreen('settings');
  }

  // Em sessão remota, o shell some — mas pedido de acesso / quem está te acessando precisam do shell.
  const attendHostUi = state.incoming !== null || state.hostSessions.length > 0;
  if (state.session?.info.role === 'viewer' && !attendHostUi) {
    return (
      <>
        <ViewerScreen
          session={state.session}
          sendInput={sendInput}
          watchRemoteCursor={controller.watchRemoteCursor}
          onChangeSettings={(patch) => controller.updateSessionSettings(patch)}
          onChangeVolume={(volume) => controller.setSessionVolume(volume)}
          onEnd={() => controller.endSession()}
          onSendFiles={(files) => controller.sendFiles(files)}
          onDropFiles={(data) => controller.sendDroppedFiles(data)}
          onDownloadCopied={() => controller.downloadCopiedFiles()}
          onCancelTransfer={() => controller.cancelTransfer()}
          onAppPaste={onAppPaste}
          onBrowserPaste={onBrowserPaste}
        />
        {state.notice && (
          <Notice kind={state.notice.kind} text={state.notice.text} onDismiss={() => controller.dismissNotice()} />
        )}
      </>
    );
  }

  const hosting = state.hostSessions.length > 0;
  const connectionsHint = !hosting
    ? 'Ninguém está acessando este computador'
    : state.hostSessions.length === 1
      ? '1 pessoa acessando este computador'
      : `${state.hostSessions.length} pessoas acessando este computador`;

  const navItems: Array<{ id: Screen; label: string }> = [
    { id: 'home', label: 'Início' },
    ...(canHost ? [{ id: 'connections' as const, label: 'Conexões' }] : []),
  ];

  function renderScreen() {
    if (inviteToken) {
      return (
        <InviteScreen
          token={inviteToken}
          user={state.user}
          onLogin={(email, password) => controller.login(email, password)}
          onSignup={(name, email, password) => controller.signup(name, email, password)}
          onLogout={() => controller.logout()}
          onAccepted={async (org) => {
            await controller.refreshMe();
            setFocusOrgId(org.id);
            closeInvite();
            openSettings('overview');
          }}
          onDismiss={() => {
            closeInvite();
            setScreen('home');
          }}
        />
      );
    }
    if (screen === 'settings') {
      return (
        <SettingsScreen
          tab={settingsTab}
          onTabChange={setSettingsTab}
          user={state.user}
          organizations={state.organizations}
          focusOrgId={focusOrgId}
          canConnect={state.connection === 'online'}
          onConnect={(code) => controller.connectTo(code)}
          onOrganizationsChanged={() => controller.refreshMe()}
          onAccountUpdated={(user) => controller.applyUser(user)}
          onLogin={(email, password) => controller.login(email, password)}
          onSignup={(name, email, password) => controller.signup(name, email, password)}
        />
      );
    }
    if (screen === 'connections') {
      return (
        <HostSessionScreen
          connections={state.hostSessions}
          onDisconnect={(sessionIds) => controller.endHostSessions(sessionIds)}
        />
      );
    }
    if (screen === 'login') {
      return (
        <LoginScreen
          onLogin={(email, password) => controller.login(email, password)}
          onSignup={(name, email, password) => controller.signup(name, email, password)}
          intro="Com uma conta você cria sua empresa, convida colaboradores e acessa os computadores liberados para você."
        />
      );
    }
    return (
      <HomeScreen
        state={state}
        computerName={INSIDE_APP ? (info?.computerName ?? '') : browserDeviceName()}
        canHost={INSIDE_APP || PREVIEW_HOST}
        onConnect={(targetId) => controller.connectTo(targetId)}
        onSaveAccessPassword={(password) => controller.setAccessPassword(password)}
        onLeaveOrganization={(organizationId) => controller.leaveOrganization(organizationId)}
        onSetUnattended={(organizationId, allowed) => controller.setUnattended(organizationId, allowed)}
        onOpenLogin={() => setScreen('login')}
      />
    );
  }

  return (
    <main className={styles.shell}>
      <header className={styles.header}>
        <div className={styles.headerLeft}>
          <span className={styles.brandRow}>
            <span className={styles.brand}>Viewer</span>
            {PREVIEW_HOST && <span className={styles.previewBadge}>prévia no navegador</span>}
          </span>
          {!inviteToken && (
            <nav className={styles.nav} aria-label="Seções">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`${styles.navItem} ${screen === item.id ? styles.navItemActive : ''}`}
                  title={item.id === 'connections' ? connectionsHint : undefined}
                  onClick={() => setScreen(item.id)}
                >
                  {item.label}
                  {item.id === 'connections' && <StatusDot tone={hosting ? 'danger' : 'idle'} />}
                </button>
              ))}
            </nav>
          )}
        </div>
        <div className={styles.headerRight}>
          {state.user ? (
            <button
              type="button"
              className={`${styles.accountButton} ${screen === 'settings' && settingsTab === 'account' ? styles.accountButtonActive : ''}`}
              title={state.user.email}
              onClick={() => openSettings('account')}
            >
              <UserIcon />
              <span>{state.user.name}</span>
            </button>
          ) : (
            !inviteToken &&
            screen !== 'login' && (
              <button type="button" className={styles.navItem} onClick={() => setScreen('login')}>
                Entrar
              </button>
            )
          )}
          {!inviteToken && (
            <button
              type="button"
              className={`${styles.iconButton} ${screen === 'settings' ? styles.iconButtonActive : ''}`}
              aria-label="Configurações"
              title="Configurações"
              onClick={() => openSettings(settingsTab === 'account' ? 'overview' : settingsTab)}
            >
              <SettingsGearIcon />
            </button>
          )}
          {canHost && !inviteToken && (
            <button
              type="button"
              className={styles.iconButton}
              aria-label="Vincular empresa"
              title="Vincular empresa"
              onClick={() => setEnrollOpen(true)}
            >
              <EnrollIcon />
            </button>
          )}
          {state.user && (
            <button type="button" className={styles.navItem} onClick={() => void controller.logout()}>
              Sair
            </button>
          )}
          {info && (
            <span className={styles.meta}>
              v{info.version}
              {info.profile !== 'default' && ` · perfil ${info.profile}`}
            </span>
          )}
        </div>
      </header>

      <div
        className={`${styles.content} ${screen === 'home' ? styles.contentFill : ''} ${
          screen === 'settings' || screen === 'connections' ? styles.contentTop : ''
        }`}
      >
        {renderScreen()}
      </div>

      {enrollOpen && (
        <Modal
          title={state.deviceOrganizations.length > 0 ? 'Vincular a outra empresa' : 'Vincular a uma empresa'}
          onClose={() => {
            if (!state.enrolling) setEnrollOpen(false);
          }}
        >
          <p className={styles.enrollIntro}>
            Coloca este computador sob uma empresa. Quem ela autorizar passa a encontrá-lo na lista.
          </p>
          <EnrollDeviceForm
            enrolling={state.enrolling}
            onEnroll={(code) => controller.enrollDevice(code)}
            onCancel={() => {
              if (!state.enrolling) setEnrollOpen(false);
            }}
          />
        </Modal>
      )}

      {state.incoming && (
        <IncomingRequestDialog
          request={state.incoming}
          passwordEnabled={state.accessPassword}
          onAccept={() => controller.acceptIncoming()}
          onReject={() => controller.rejectIncoming()}
        />
      )}
      {state.outgoing && (
        <OutgoingRequestDialog
          request={state.outgoing}
          onSubmitPassword={(password) => controller.submitPassword(password)}
          onCancel={() => controller.cancelOutgoing()}
        />
      )}

      {state.notice && (
        <Notice kind={state.notice.kind} text={state.notice.text} onDismiss={() => controller.dismissNotice()} />
      )}
    </main>
  );
}
